import getRecapGridTotals from 'src/components/ui/Grid/PrepGrid/GridFunctions/GridUpdate/getRecapGridTotals'
import * as mutations from 'src/graphql/mutations'
import * as queries from 'src/graphql/queries'
import type { ReduxQuote, ReduxRecapInfo } from 'src/types/apiType'
import type { QuoteType, UserInfo } from 'src/types/miscType'
import { API, graphqlOperation } from 'src/utils/amplify/legacy'
import {
	fetchQuoteInfoUtil,
	fetchRecapInfoUtil,
	getFetchFilter,
} from 'src/utils/fetchUtils'
import getQuoteTotalSummary from 'src/utils/quote/quoteTotalsSummary/getQuoteTotalSummary'
import {
	getProperUpdateValues,
	hasBusinessChanges,
} from 'src/utils/updateApiUtil'
import type { ModelQuoteInfoFilterInput } from '@/API'

async function getProjectQuoteTotals({
	quoteId,
	fetchFilter,
}: {
	quoteId: string
	fetchFilter?: ModelQuoteInfoFilterInput | null
}) {
	// Finds quote from the id
	const {
		data: { getQuote: quoteDetails },
	} = (await API.graphql(
		graphqlOperation(queries.getQuote, { id: quoteId })
	)) as {
		data: { getQuote: ReduxQuote }
	}

	const quoteInfo = await fetchQuoteInfoUtil(quoteId, fetchFilter)
	const recapInfo = await fetchRecapInfoUtil(quoteId, fetchFilter)
	const quoteTotals = getQuoteTotalSummary({
		quoteDetails,
		quoteInfo,
		recapInfo,
	})

	return quoteTotals
}

function getQuoteVersion(quoteType: QuoteType) {
	switch (quoteType) {
		case 'frenchApfp':
			return 1.7
		case 'frenchPostProd':
			return 1.4
		case 'aicp':
			return 1.3
		case 'photoGrid':
			return 1.4
		case 'soundProd':
			return 1.1
		case 'cnc5ch':
			return 1.1
		case 'quoteAnimationOnly':
			return 1.0
		case 'quoteAnimationAndLiveAction':
			return 1.0
		case 'blankQuote':
			return 1.0
		case 'quoteDigital':
			return 1.1
		case '360Template':
			return 1.0
		default: {
			const tsCheck: never = quoteType
			return 1.0
		}
	}
}

async function updateRecapTotals({
	quoteId,
	userInfo,
}: {
	quoteId: string
	userInfo: UserInfo
}) {
	try {
		const quoteFilter = getFetchFilter(userInfo)
		const {
			data: { getQuote },
		} = (await API.graphql(
			graphqlOperation(queries.getQuote, { id: quoteId })
		)) as {
			data: { getQuote: ReduxQuote }
		}
		if (!getQuote?.quoteType) {
			throw new Error(`Quote type not defined', ${getQuote}`)
		}
		const recapInfoCostlines: ReduxRecapInfo[] = await fetchRecapInfoUtil(
			quoteId,
			quoteFilter
		)
		const updatedRecapCostlines = getRecapGridTotals({
			localRows: recapInfoCostlines,
			quoteType: getQuote.quoteType,
			quoteDetails: getQuote,
		})
		console.log('updatedRecapCostlines: ', updatedRecapCostlines)
		for (let i = 0; i < updatedRecapCostlines.length; i++) {
			const eachRow = updatedRecapCostlines[i]
			const originalRow = recapInfoCostlines.find(
				(row) => row.id === eachRow.id
			)
			if (originalRow) {
				const updatedRecapInfoParams =
					getProperUpdateValues<ReduxRecapInfo>({
						updatedInput: eachRow,
						originalInput: originalRow,
					})
				if (hasBusinessChanges(updatedRecapInfoParams)) {
					console.log(
						'updatedRecapInfoParams: ',
						updatedRecapInfoParams
					)
					await API.graphql(
						graphqlOperation(mutations.updateRecapInfo, {
							input: updatedRecapInfoParams,
						})
					)
				}
			}
		}
	} catch (error: any) {
		console.error('error in updating Recap totals: ', error)
	}
}

export { getProjectQuoteTotals, getQuoteVersion, updateRecapTotals }
